import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { showSnackbar } from "../../store/snackbarSlice/snackbarSlice";
import { useDispatch } from "react-redux";

const LookupAction = ({
  controllerUrl,
  createLabelButton,
  createLabel,
  updateLabel,
}) => {
  const dispatch = useDispatch();

  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  // modal state
  const [open, setOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [form, setForm] = useState({ name: "" });

  const [errors, setErrors] = useState({});

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedAction(null);
    setForm({ name: "" });
  };

  const loadData = async () => {
    try {
      const response = await callBackendApi(`${controllerUrl}`, "GET");
      setActions(response.data);
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ---- CREATE ----
  const createActionModal = () => {
    setSelectedAction(null);
    setForm({ name: "" });
    handleOpen();
  };

  // ---- EDIT ----
  const updateActionModal = (id) => {
    const found = actions.find((a) => a.id === id);

    if (!found) {
      return dispatch(showSnackbar({ message: "Could not find item." }));
    }

    setSelectedAction(found);
    setForm({ name: found.name });
    handleOpen();
  };

  // ---- SAVE (Create or Edit) ----
  const saveAction = async () => {
    setErrors({});

    try {
      const url = selectedAction
        ? `${controllerUrl}/${selectedAction.id}`
        : `${controllerUrl}`;

      const method = selectedAction ? "PUT" : "POST";

      const response = await callBackendApi(url, method, form);

      if (response && response.success) {
        dispatch(showSnackbar({ message: "Saved successfully!" }));
        handleClose();
        loadData();
      } else if (response && response.error) {
        setErrors(response.error);
        dispatch(
          showSnackbar({
            message: "There are validation errors. Please check the form.",
          })
        );
      } else {
        dispatch(
          showSnackbar({
            message: "Something went wrong while saving.",
          })
        );
      }
    } catch (error) {
      dispatch(showSnackbar({ message: "Request error." }));
    }
  };

  // ---- DELETE ----
  const handleDelete = async (id) => {
    try {
      const confirmDelete = window.confirm(
        "Are you sure you want to delete this?"
      );
      if (!confirmDelete) return;

      const response = await callBackendApi(`${controllerUrl}/${id}`, "DELETE");

      if (response && response.success) {
        dispatch(showSnackbar({ message: "Deleted successfully!" }));
        loadData();
      } else {
        dispatch(
          showSnackbar({
            message: "Something went wrong while deleting.",
          })
        );
      }
    } catch (error) {
      dispatch(showSnackbar({ message: "Request error." }));
    }
  };

  // ---- TABLE COLUMNS ----
  const columns = [
    { field: "id", headerName: "Id", width: 120 },
    { field: "name", headerName: "Name", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <IconButton
            onClick={() => updateActionModal(params.row.id)}
            color="primary"
          >
            <Edit />
          </IconButton>

          <IconButton onClick={() => handleDelete(params.row.id)} color="error">
            <Delete />
          </IconButton>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "1700px", margin: "auto" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Button variant="contained" onClick={createActionModal}>
            {createLabelButton}
          </Button>
        </Grid>

        <Grid item xs={12}>
          <DataGrid
            rows={actions}
            columns={columns}
            pageSize={10}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            autoHeight
          />
        </Grid>
      </Grid>

      {/* ----------------------- MODAL ------------------------ */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{selectedAction ? updateLabel : createLabel}</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={Boolean(errors.name)}
            helperText={errors.name ? errors.name[0] : ""}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={saveAction}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* ------------------------------------------------------- */}
    </Box>
  );
};

export default LookupAction;
